import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import PubSub from 'pubsub-js';
import { useRouter } from 'next/router';
import { CSSTransition } from 'react-transition-group';
import { useQuery, useApolloClient } from '@apollo/client';
import { CURRENT_USER } from '../App.gql';
import { useIdentity } from '../Authentication/Authentication.hooks';

import { PRODUCT_VARIANT_COUNT } from './Navigation.gql';
import { refreshSession } from '../Authentication/Authentication.utils';
import { useAuthState } from '../Authentication/Authentication.context';
import NavDrawer from './components/NavDrawer/NavDrawer';
import NavFlyout from './components/NavFlyout/NavFlyout';
import { flattenSections } from './components/NavDrawer/NavDrawer.utils';
import { BUILD } from '../../util/const';
import { vercel_base_url } from '../../util/config';
import { getVariantCount } from '../Cart/Cart.utils';
import LinkText from '../LinkText/LinkText';

import NotificationIcon from '../Icons/NotificationIcon/NotificationIcon';
import * as Styled from './Navigation.styled';

/**
 * This component displays the top level navigation for the site.
 */
const Navigation = ({ sections, withSearch, onRequestOpen }) => {
  const { setIsDialogOpen } = useAuthState();
  const [isNavDrawerOpen, setIsNavDrawerOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [isFlyoutOpen, setIsFlyoutOpen] = useState(false);
  const [currentSection, setCurrentSection] = useState(null);
  const flyoutAnimationStyle = useRef('piano');
  const previousSectionRef = useRef(null);
  const flyoutIdleTimer = useRef(null);
  const flyoutCloseTimer = useRef(null);
  const FLYOUT_CLOSE_DELAY = 300; // milliseconds
  const router = useRouter();

  /** Testing Purposes for NavFlyout
   * Sets NavFlyout to always be open after hovering a NavLink
   * Can still be closed via Escape key upon tabbing into it */
  const urlParams = isMounted && new URLSearchParams(window.location.search);
  const alwaysOpened = urlParams && urlParams.get('open') === 'true';

  const client = useApolloClient();

  const { user } = useIdentity();
  const { data } = useQuery(CURRENT_USER);
  const cartId = data?.cartId;

  const { data: cartData } = useQuery(PRODUCT_VARIANT_COUNT, {
    context: { service: 'storefront' },
    variables: { cartId },
    skip: !cartId
  });

  const productVariantCount = getVariantCount(cartData?.cart?.lines?.edges);

  const handleButtonClick = (e) => {
    e.preventDefault();
    const path = e.currentTarget.getAttribute('href');
    if (path) router.push(path);
  };

  const cancelCloseFlyout = () => {
    if (flyoutCloseTimer.current) {
      clearTimeout(flyoutCloseTimer.current);
      flyoutCloseTimer.current = null;
    }
  };

  const openFlyout = useCallback(
    (section) => {
      cancelCloseFlyout();
      setIsFlyoutOpen(true);
      if (currentSection !== null) {
        previousSectionRef.current = currentSection;
      }
      setCurrentSection(section);
    },
    [currentSection]
  );

  const closeFlyout = useCallback(() => {
    if (!alwaysOpened) {
      setIsFlyoutOpen(false);
      previousSectionRef.current = currentSection;
    }
  }, [alwaysOpened, currentSection]);

  const scheduleCloseFlyout = () => {
    if (alwaysOpened) return;

    cancelCloseFlyout();
    flyoutCloseTimer.current = setTimeout(() => {
      closeFlyout();
    }, FLYOUT_CLOSE_DELAY);
  };

  useEffect(() => {
    setIsMounted(true);

    return () => {
      setIsMounted(false);
      cancelCloseFlyout();
      if (flyoutIdleTimer.current) {
        clearTimeout(flyoutIdleTimer.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isFlyoutOpen) {
      if (flyoutIdleTimer.current) {
        clearTimeout(flyoutIdleTimer.current);
        flyoutIdleTimer.current = null;
      }

      if (previousSectionRef.current !== null)
        flyoutAnimationStyle.current = 'fade';
    } else {
      if (flyoutIdleTimer.current) {
        clearTimeout(flyoutIdleTimer.current);
      }

      flyoutIdleTimer.current = setTimeout(() => {
        flyoutAnimationStyle.current = 'piano';
        previousSectionRef.current = null;
        flyoutIdleTimer.current = null;
      }, 5000);
    }

    // Cleanup on unmount
    return () => {
      if (flyoutIdleTimer.current) {
        clearTimeout(flyoutIdleTimer.current);
        flyoutIdleTimer.current = null;
      }
    };
  }, [isFlyoutOpen, currentSection]);

  const slidingPanels = flattenSections(sections);

  return (
    <Styled.Navigation data-testid="Navigation">
      <Styled.NavigationBar>
        <Styled.LogoContainer>
          <Styled.LogoLink
            onMouseEnter={() => {
              if (!alwaysOpened && isFlyoutOpen) {
                closeFlyout();
              }
            }}
            href={BUILD === 'shopify' ? vercel_base_url : '/'}
            aria-label="Bombas"
            as={BUILD === 'shopify' ? 'a' : undefined}
          >
            <Styled.Icon name="logo-type" size={22} />
          </Styled.LogoLink>
        </Styled.LogoContainer>

        <Styled.LeftIcons>
          <Styled.ButtonIcon
            name="burger"
            iconSize={22}
            onClick={() => {
              setIsNavDrawerOpen(true);
            }}
            data-track-label="Hamburger"
            data-track-property="open"
            aria-label="Site Navigation"
          />

          {withSearch && (
            <Styled.LeftSearchIcon
              name="search"
              iconSize={22}
              onClick={onRequestOpen}
              data-track-label="Search"
              data-track-property="open"
              data-testid="headerSearch"
              aria-label="Search"
            />
          )}
        </Styled.LeftIcons>

        <Styled.DesktopLinkContainer
          onMouseEnter={cancelCloseFlyout}
          onMouseLeave={scheduleCloseFlyout}
        >
          {sections.map((section, idx) => (
            <Styled.NavLink
              key={idx}
              size="Mini"
              variant="title"
              href={section.url}
              data-label="navLink_L1"
              onClick={handleButtonClick}
              onMouseEnter={() => openFlyout(section)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  openFlyout(section);
                }
                if (e.key === 'Escape') {
                  closeFlyout();
                }
              }}
              active={section === currentSection}
            >
              {section.text}
            </Styled.NavLink>
          ))}
        </Styled.DesktopLinkContainer>

        <Styled.RightIcons>
          <Styled.RightInnerIcons
            onMouseEnter={() => {
              if (!alwaysOpened && isFlyoutOpen) {
                closeFlyout();
              }
            }}
          >
            {isMounted && user.isLoggedIn && (
              <>
                <Styled.BuyAgainLink
                  as={BUILD === 'shopify' ? 'a' : LinkText}
                  href={
                    BUILD === 'shopify'
                      ? `${vercel_base_url}/buy-again`
                      : '/buy-again'
                  }
                  size="Mini"
                  variant="title"
                >
                  Buy Again
                </Styled.BuyAgainLink>
                <Styled.ButtonIcon
                  name="account"
                  iconSize={22}
                  onClick={async (e) => {
                    e.preventDefault();
                    refreshSession(client);
                    PubSub.publish('account_nav.open');
                    PubSub.publish('cart.close');
                  }}
                  data-track-label="My Account"
                  aria-label="My Account"
                />
              </>
            )}
            {isMounted && !user.isLoggedIn && (
              <Styled.LogInButton
                variant="secondary"
                isGhost
                onClick={() => setIsDialogOpen(true)}
                data-track-label="Log In"
                aria-label="Log In"
              >
                Log In
              </Styled.LogInButton>
            )}
            {withSearch && (
              <Styled.RightSearchIcon
                name="search"
                iconSize={22}
                onClick={onRequestOpen}
                data-track-label="Search"
                data-track-property="open"
                data-testid="headerSearch"
                aria-label="Search"
              />
            )}
            <NotificationIcon
              name="bag"
              count={productVariantCount}
              onClick={() => {
                PubSub.publish('cart.open');
                PubSub.publish('account_nav.close');
              }}
              data-track-label="Cart"
              data-track-property="open"
              aria-label="Cart"
              data-label="miniCart_open"
            />
          </Styled.RightInnerIcons>
        </Styled.RightIcons>
      </Styled.NavigationBar>

      {isMounted && (
        <CSSTransition
          in={isFlyoutOpen}
          timeout={200}
          classNames="flyout-container"
          unmountOnExit
          onExited={() => {
            setCurrentSection(null);
          }}
        >
          <NavFlyout
            flyoutAnimationStyle={flyoutAnimationStyle.current}
            level1Section={currentSection}
            onMouseEnter={cancelCloseFlyout}
            onMouseLeave={scheduleCloseFlyout}
            onBackdropEnter={() => {
              if (!alwaysOpened) {
                closeFlyout();
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                closeFlyout();
              }
            }}
          />
        </CSSTransition>
      )}

      {isMounted && (
        <NavDrawer
          isOpen={isNavDrawerOpen}
          isLoggedIn={user.isLoggedIn}
          onRequestClose={() => setIsNavDrawerOpen(false)}
          rootPanel={sections}
          slidingPanels={slidingPanels}
        />
      )}
    </Styled.Navigation>
  );
};

Navigation.propTypes = {
  /**
   * Array of Navigation Sections
   */
  sections: PropTypes.arrayOf(PropTypes.shape({})),
  /**
   * Hides the search button if false
   */
  withSearch: PropTypes.bool,
  /**
   * Opens Search and hides Navigation
   */
  onRequestOpen: PropTypes.func
};

Navigation.defaultProps = {
  sections: [],
  withSearch: true,
  onRequestOpen: undefined
};

export default Navigation;
